// Per-route SEO metadata. Used by <RouteSeo /> to render Helmet tags.

export const SITE_URL = "https://guidefrancophonesiemreap.com";
export const SITE_NAME = "Aṅkor Sojourns";
export const DEFAULT_OG_IMAGE =
  "https://pub-bb2e103a32db4e198524a2e9ed8f35b4.r2.dev/b7448ec9-9a73-4fcf-ba0e-1ad690ffbaee/id-preview-8db759b1--21fd9441-9c20-44c7-8b19-6fa747d960af.lovable.app-1778211786997.png";

export type SeoEntry = {
  title: string;
  description: string;
  type?: "website" | "article";
  // Optional TouristTrip schema fields for tour pages
  trip?: {
    name: string;
    description: string;
    duration?: string; // ISO 8601 e.g. P1D
    priceFrom?: number;
    currency?: string;
  };
};

const tour = (
  name: string,
  description: string,
  duration: string,
  priceFrom?: number,
): SeoEntry => ({
  title: `${name} — ${SITE_NAME}`.slice(0, 60),
  description,
  type: "article",
  trip: { name, description, duration, priceFrom, currency: "USD" },
});

export const SEO: Record<string, SeoEntry> = {
  "/": {
    title: "Guide francophone Angkor Wat & Cambodge — Aṅkor Sojourns",
    description:
      "Guide francophone à Siem Reap & chauffeur privé au Cambodge. Circuits sur mesure 1 à 30 jours : Angkor Wat, Phnom Kulen, Tonlé Sap, Koh Rong, Phnom Penh.",
    type: "website",
  },
  "/tours": {
    title: "Cambodia Tours & Itineraries — Aṅkor Sojourns",
    description:
      "Browse all Cambodia tours: half-day Angkor Wat sunrise to 30-day cross-country journeys. Private guides, tailor-made itineraries, small groups.",
    type: "website",
  },
  "/unsubscribe": {
    title: "Email Unsubscribe — Aṅkor Sojourns",
    description: "Manage your email subscription preferences for Aṅkor Sojourns.",
    type: "website",
  },
  "/blog": {
    title: "Cambodia Travel Blog & Journal — Aṅkor Sojourns",
    description:
      "Long-form guides to Angkor Wat, Siem Reap, Phnom Kulen and slow travel in Cambodia — written by a local French-speaking private guide.",
    type: "website",
  },
  "/tours/one-day-angkor-wat": tour(
    "One Day Angkor Wat Sunrise Tour",
    "Sunrise at Angkor Wat plus Ta Prohm, Bayon and Angkor Thom — a private 8-hour day with an expert guide and hotel pickup.",
    "PT8H",
    135,
  ),
  "/tours/banteay-srei-grand-circuit": tour(
    "Banteay Srei & Grand Circuit Tour",
    "Pink-sandstone Banteay Srei plus the Grand Circuit temples — Preah Khan, Neak Pean, Ta Som and East Mebon — in a single private day.",
    "PT9H",
  ),
  "/tours/phnom-kulen-waterfall": tour(
    "Phnom Kulen Waterfall Day Tour",
    "Reclining Buddha, River of 1000 Lingas and Phnom Kulen waterfall — a sacred mountain day trip from Siem Reap.",
    "PT10H",
  ),
  "/tours/kampong-kleang": tour(
    "Kampong Kleang Floating Village Tour",
    "Visit the stilt-house village of Kampong Kleang on the Tonlé Sap — Cambodia's largest freshwater lake — by private car and boat.",
    "PT6H",
  ),
  "/tours/siem-reap-2-day": tour(
    "Siem Reap 2-Day Tour: Angkor & Kampong Phluk",
    "Two-day Siem Reap private tour: Angkor Wat sunrise, Bayon, Ta Prohm and a Kampong Phluk floating-village afternoon.",
    "P2D",
  ),
  "/tours/siem-reap-2-day-kulen": tour(
    "Siem Reap 2-Day Tour: Angkor & Phnom Kulen",
    "Two days combining the Angkor temples with sacred Phnom Kulen — waterfall, Reclining Buddha and River of 1000 Lingas.",
    "P2D",
  ),
  "/tours/siem-reap-3-day-kulen-phluk": tour(
    "Siem Reap 3 Days: Angkor, Kulen & Kampong Phluk",
    "Three-day Siem Reap deep dive: Angkor temples, Phnom Kulen mountain and Kampong Phluk floating village by private guide.",
    "P3D",
  ),
  "/tours/siem-reap-three-day": tour(
    "Siem Reap 3 Day Tour",
    "Three days exploring Angkor temples, hidden ruins and Khmer village life with a private francophone guide.",
    "P3D",
  ),
  "/tours/siem-reap-4-day": tour(
    "Siem Reap 4 Day Tour",
    "Four-day private Siem Reap itinerary: Angkor Wat, Banteay Srei, Tonlé Sap and the lesser-visited temple circuits.",
    "P4D",
  ),
  "/tours/siem-reap-4-day-beng-mealea": tour(
    "Siem Reap 4 Days + Beng Mealea Jungle Temple",
    "Four days across Siem Reap including the jungle-clad Beng Mealea, Banteay Srei and a sunrise at Angkor Wat.",
    "P4D",
  ),
  "/tours/cambodia-7-day": tour(
    "Cambodia 7 Day Tour: Siem Reap & Phnom Penh",
    "One-week Cambodia private tour: Angkor temples, Tonlé Sap, Phnom Penh's Royal Palace and the Killing Fields memorial.",
    "P7D",
  ),
  "/tours/cambodia-10-day": tour(
    "Cambodia 10 Day Tour: Temples, Capital & Coast",
    "Ten days across Cambodia: Angkor Wat, Phnom Penh, Kampot pepper farms and the southern coast — fully private.",
    "P10D",
  ),
  "/tours/cambodia-18-day": tour(
    "Cambodia 18 Day Tour: Comprehensive Loop",
    "Eighteen-day Cambodia loop: Siem Reap, Battambang, Phnom Penh, Kep, Kampot and the Cardamom highlands.",
    "P18D",
  ),
  "/tours/cambodia-21-day": tour(
    "Cambodia 21 Day Tailor-Made Tour",
    "Three-week tailor-made Cambodia circuit: temples, river villages, capital city, southern coast and island time.",
    "P21D",
  ),
  "/tours/cambodia-25-day": tour(
    "Cambodia 25 Day Cultural & Nature Tour",
    "Twenty-five days deep across Cambodia — Angkor, Mondulkiri elephants, Mekong dolphins, Cardamoms and Koh Rong.",
    "P25D",
  ),
  "/tours/cambodia-30-day": tour(
    "Cambodia 30 Day Ultimate Tour",
    "A month-long, fully private Cambodia odyssey covering every region — temples, jungle, river, mountain and sea.",
    "P30D",
  ),
  "/tours/khmer-village-monk-blessing": tour(
    "Khmer Village & Monk Blessing Tour",
    "Visit a traditional Khmer village near Siem Reap and receive a water blessing from Buddhist monks at a working pagoda.",
    "PT5H",
  ),
  "/tours/village-oxcart-farm": tour(
    "Chreav Village Ox-Cart & Rice Farm Tour",
    "Half-day Chreav village tour: ride a wooden ox-cart, visit a rice mill and meet farming families outside Siem Reap.",
    "PT5H",
  ),
  "/tours/life-farm-homestay": tour(
    "Life on the Farm Homestay",
    "Overnight homestay with a Khmer farming family near Siem Reap — cooking, fishing, rice harvesting by season.",
    "P2D",
  ),
  "/tours/prek-toal-bird-sanctuary": tour(
    "Prek Toal Bird Sanctuary Day Tour",
    "Day trip to the Prek Toal core biosphere on Tonlé Sap — Southeast Asia's most important waterbird breeding ground.",
    "PT10H",
  ),
  "/tours/prek-toal-homestay": tour(
    "Prek Toal Floating Village Homestay",
    "Overnight homestay in the floating village of Prek Toal — sunrise birdwatching at the Tonlé Sap biosphere reserve.",
    "P2D",
  ),
  "/tours/kampong-phluk-homestay": tour(
    "Kampong Phluk Stilt-House Homestay",
    "Overnight stay in a stilt-house family home at Kampong Phluk — Tonlé Sap sunset and flooded-forest paddle.",
    "P2D",
  ),
  "/tours/kampong-khleang-homestay": tour(
    "Kampong Khleang Tonlé Sap Homestay",
    "Overnight Kampong Khleang homestay — Cambodia's largest stilt-house community on the great Tonlé Sap lake.",
    "P2D",
  ),
  "/tours/phnom-kulen-srah-damrei": tour(
    "Phnom Kulen + Srah Damrei Elephant Pond Tour",
    "Phnom Kulen with the remote Srah Damrei carved-elephant rocks — a full jungle-trek day from Siem Reap.",
    "P1D",
  ),
};
